import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  Person,
  CreatePersonPayload,
  InvitePersonPayload,
  InvitePersonResponse,
  PaginatedResponse,
} from '@/lib/types';

export interface PersonFilters {
  page?: number;
  page_size?: number;
  q?: string;
  ordering?: string;
}

export function usePersons(filters: PersonFilters = {}, enabled: boolean = true) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.persons.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<Person>> => {
      const response = await apiClient.get<PaginatedResponse<Person>>(
        '/api/v1/persons/',
        { params: cleanFilters }
      );
      return response.data;
    },
    enabled,
  });
}

export function useAllPersons(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.persons.list({ scope: 'all' }),
    queryFn: async (): Promise<Person[]> => {
      const persons: Person[] = [];
      let nextUrl: string | null = '/api/v1/persons/';
      let isFirstPage = true;

      while (nextUrl) {
        const response: { data: PaginatedResponse<Person> } = await apiClient.get(
          nextUrl,
          isFirstPage ? { params: { page_size: 1000 } } : undefined
        );
        persons.push(...response.data.results);
        nextUrl = response.data.next;
        isFirstPage = false;
      }

      return persons;
    },
    enabled,
  });
}

export function usePerson(id: number) {
  return useQuery({
    queryKey: queryKeys.persons.detail(id),
    queryFn: async (): Promise<Person> => {
      const response = await apiClient.get<Person>(`/api/v1/persons/${id}/`);
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePersonPayload): Promise<Person> => {
      const response = await apiClient.post<Person>('/api/v1/persons/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
    },
  });
}

export function useUpdatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreatePersonPayload>;
    }): Promise<Person> => {
      const response = await apiClient.patch<Person>(`/api/v1/persons/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.persons.detail(variables.id) });
    },
  });
}

export function useInvitePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data = {},
    }: {
      id: number;
      data?: InvitePersonPayload;
    }): Promise<InvitePersonResponse> => {
      const response = await apiClient.post<InvitePersonResponse>(
        `/api/v1/admin/persons/${id}/invite/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.invitations.all });
    },
  });
}

export function useDeletePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/persons/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
    },
  });
}
